import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { SMOKE_SOURCE_NODE } from "./decorSmoke";

const flat = (color: string, extra: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.75, metalness: 0, flatShading: true, ...extra });

const red = flat("#b3261e");
const maroon = flat("#7a1f1a");
const boilerGreen = flat("#1f4d3a");
const black = flat("#1d1d1f", { roughness: 0.6 });
const brass = flat("#d9a93a", { metalness: 0.55, roughness: 0.35 });
const wood = flat("#9a6a3f");
const cream = flat("#f1e6c8");
const yellow = flat("#e8b931");
const coal = flat("#262322", { roughness: 1 });
const windows = flat("#2b3540");

const GAUGE = 1.1;
const WHEEL_X = GAUGE / 2 + 0.05;

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

function box(width: number, height: number, depth: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  geometry.translate(x, y, z);
  return geometry;
}

/** Cylindre vertical dont `y` est la base. */
function post(radius: number, height: number, x: number, y: number, z: number, segments = 8): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(radius, radius, height, segments);
  geometry.translate(x, y + height / 2, z);
  return geometry;
}

/** Cylindre couché le long de z, centré sur (`x`, `y`, `z`). */
function barrel(radius: number, length: number, x: number, y: number, z: number, segments = 16): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(radius, radius, length, segments);
  geometry.rotateX(Math.PI / 2);
  geometry.translate(x, y, z);
  return geometry;
}

/** Une paire de roues sur un même essieu, posées sur le rail. */
function wheelPair(radius: number, z: number): THREE.BufferGeometry[] {
  return [-1, 1].map((side) => {
    const wheel = new THREE.CylinderGeometry(radius, radius, 0.14, 14);
    wheel.rotateZ(Math.PI / 2);
    wheel.translate(side * WHEEL_X, radius, z);
    return wheel;
  });
}

/** Locomotive à vapeur américaine du XIXᵉ siècle, cheminée en diamant et chasse-pierres ; environ 9 m, regarde vers +z. */
export function buildSteamLocomotive(): THREE.Group {
  const root = new THREE.Group();
  const boilerY = 2.25;

  root.add(merged([box(2, 0.35, 8.2, 0, 1.05, 0.2)], black, "Frame"));
  root.add(merged([
    ...[-0.9, 0.7, 2.3].flatMap((z) => wheelPair(0.78, z - 1)),
    ...[3.2, 3.9].flatMap((z) => wheelPair(0.42, z)),
  ], red, "Wheels"));
  root.add(merged([-1, 1].map((side) => box(0.08, 0.12, 3.4, side * (WHEEL_X + 0.1), 0.78, 0.1)), brass, "Rods"));

  root.add(merged([barrel(0.82, 4.4, 0, boilerY, 1.7)], boilerGreen, "Boiler"));
  root.add(merged([
    barrel(0.86, 0.9, 0, boilerY, 4.3),
    post(0.22, 0.9, 0, boilerY + 0.6, 3.9),
    (() => {
      const stack = new THREE.CylinderGeometry(0.6, 0.22, 1.1, 12);
      stack.translate(0, boilerY + 2.05, 3.9);
      return stack;
    })(),
  ], black, "Smokebox"));
  root.add(merged([
    ...[0.6, 2.2, 3.4].map((z) => barrel(0.86, 0.12, 0, boilerY, z)),
    post(0.34, 0.6, 0, boilerY + 0.65, 1.9, 12),
    new THREE.SphereGeometry(0.34, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2).translate(0, boilerY + 1.25, 1.9),
    new THREE.SphereGeometry(0.2, 10, 8).translate(0, boilerY + 1.1, 2.9),
    box(0.55, 0.5, 0.45, 0, boilerY + 1.1, 4.55),
  ], brass, "Brass"));

  const pilot = new THREE.ConeGeometry(1.15, 1.1, 4, 1);
  pilot.rotateX(Math.PI / 2);
  pilot.rotateZ(Math.PI / 4);
  pilot.scale(1, 0.6, 1);
  pilot.translate(0, 0.62, 5.05);
  root.add(merged([pilot, box(2.1, 0.3, 0.3, 0, 1.15, 4.6)], red, "Pilot"));

  const cabZ = -2.3;
  root.add(merged([
    box(2.3, 1.1, 2.4, 0, 1.75, cabZ),
    ...[-1, 1].map((side) => box(0.12, 1.1, 2.4, side * 1.09, 2.85, cabZ)),
    box(2.3, 1.1, 0.12, 0, 2.85, cabZ + 1.14),
  ], maroon, "Cab"));
  root.add(merged([box(2.7, 0.14, 2.9, 0, 3.47, cabZ), box(2.5, 0.2, 2.7, 0, 3.6, cabZ)], black, "CabRoof"));
  root.add(merged([-1, 1].map((side) => box(0.14, 0.7, 1.4, side * 1.09, 2.9, cabZ + 0.1)), windows, "CabWindows"));

  const smokeSource = new THREE.Object3D();
  smokeSource.name = SMOKE_SOURCE_NODE;
  smokeSource.position.set(0, boilerY + 2.65, 3.9);
  root.add(smokeSource);
  return root;
}

/** Tender chargé de charbon, attelé derrière la locomotive ; environ 5 m. */
export function buildSteamTender(): THREE.Group {
  const root = new THREE.Group();
  root.add(merged([box(2, 0.3, 4.8, 0, 1.0, 0)], black, "Frame"));
  root.add(merged([-1.6, -0.9, 0.9, 1.6].flatMap((z) => wheelPair(0.42, z)), red, "Wheels"));
  root.add(merged([box(2.2, 1.2, 4.5, 0, 1.75, 0)], maroon, "Body"));
  root.add(merged([box(2.24, 0.12, 4.54, 0, 2.3, 0), box(2.24, 0.12, 4.54, 0, 1.2, 0)], brass, "Trim"));
  const pile = new THREE.SphereGeometry(1, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
  pile.scale(1, 0.5, 1.7);
  pile.translate(0, 2.35, 0.4);
  root.add(merged([pile], coal, "Coal"));
  return root;
}

/** Voiture d'excursion ouverte : bancs en travers, montants et toit rayé ; environ 9 m. */
export function buildExcursionCoach(): THREE.Group {
  const root = new THREE.Group();
  const length = 8.6;
  const half = length / 2;
  const floorY = 1.25;
  const roofY = 3.55;

  root.add(merged([box(2, 0.3, length - 0.4, 0, 1.0, 0)], black, "Frame"));
  root.add(merged([-3.4, -2.6, 2.6, 3.4].flatMap((z) => wheelPair(0.4, z)), red, "Wheels"));
  root.add(merged([
    box(2.4, 0.18, length, 0, floorY, 0),
    ...[-1, 1].map((side) => box(0.08, 0.6, length, side * 1.16, floorY + 0.38, 0)),
  ], maroon, "Body"));
  root.add(merged([
    ...[-1, 1].map((side) => box(0.1, 0.1, length, side * 1.18, floorY + 0.7, 0)),
    ...[-1, 1].flatMap((side) => Array.from({ length: 6 }, (_, i) => post(0.045, roofY - floorY, side * 1.12, floorY, -half + 0.3 + i * ((length - 0.6) / 5), 6))),
  ], brass, "Posts"));
  root.add(merged(Array.from({ length: 7 }, (_, i) => {
    const z = -half + 0.9 + i * ((length - 1.8) / 6);
    return [box(2.1, 0.12, 0.5, 0, floorY + 0.5, z), box(2.1, 0.55, 0.08, 0, floorY + 0.8, z - 0.25)];
  }).flat(), wood, "Benches"));

  const stripes = 8;
  const stripeLength = (length + 0.4) / stripes;
  const stripe = (i: number) => {
    const geometry = new THREE.CylinderGeometry(1.45, 1.45, stripeLength, 16, 1, false, -Math.PI / 2, Math.PI);
    geometry.rotateX(Math.PI / 2);
    geometry.scale(1, 0.32, 1);
    geometry.translate(0, roofY, -(length + 0.4) / 2 + (i + 0.5) * stripeLength);
    return geometry;
  };
  root.add(merged(Array.from({ length: stripes }, (_, i) => i).filter((i) => i % 2 === 0).map(stripe), yellow, "RoofStripes"));
  root.add(merged(Array.from({ length: stripes }, (_, i) => i).filter((i) => i % 2 === 1).map(stripe), cream, "Roof"));
  root.add(merged([-1, 1].map((side) => box(2.5, 0.1, 0.1, 0, roofY, side * (half + 0.15))), red, "RoofEdge"));
  return root;
}
